import { useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { articles, FAQItem, faqItems } from '@/mocks/mangaData';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();

  const article = useMemo(() => articles.find((a) => a.id === id), [id]);

  const relatedArticles = useMemo(() => {
    if (!article) return [];
    return articles
      .filter((a) => a.id !== article.id && a.categorySlug === article.categorySlug)
      .slice(0, 3);
  }, [article]);

  useEffect(() => {
    if (article) {
      document.title = `${article.title} - 禁漫天`;
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute('content', article.description);
      }
    }
  }, [article]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [id]);

  if (!article) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center bg-background-50">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-50 text-primary-500">
            <i className="ri-file-unknow-line text-2xl"></i>
          </div>
          <p className="text-foreground-600">文章不存在或已下架</p>
          <Link to="/" className="text-sm text-primary-500 hover:text-primary-600 mt-2 inline-block">
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.description,
    author: {
      '@type': 'Organization',
      name: article.author,
    },
    datePublished: article.publishDate,
    dateModified: article.updateDate,
    image: article.coverImage,
    url: `https://jinmantian.com/article/${article.id}`,
    publisher: {
      '@type': 'Organization',
      name: '禁漫天',
      url: 'https://jinmantian.com/',
    },
    inLanguage: 'zh-CN',
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'https://jinmantian.com/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: article.category,
        item: `https://jinmantian.com/category/${article.categorySlug}`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: article.title,
      },
    ],
  };

  return (
    <div className="min-h-screen bg-background-50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />

      <div className="pt-20">
        <Breadcrumb
          items={[
            { label: '首页', path: '/' },
            { label: article.category, path: `/category/${article.categorySlug}` },
            { label: article.title },
          ]}
        />
      </div>

      {/* Article Header */}
      <div className="w-full px-4 md:px-6 py-8 md:py-12">
        <div className="max-w-4xl mx-auto">
          {/* Cover Image */}
          <div className="w-full h-56 md:h-80 rounded-lg overflow-hidden mb-8">
            <img
              src={article.coverImage}
              alt={article.title}
              title={article.title}
              className="w-full h-full object-cover"
            />
          </div>

          {/* Meta */}
          <div className="mb-6">
            <span className="inline-block px-3 py-1 bg-primary-50 text-primary-600 text-xs font-medium rounded-full mb-4">
              {article.category}
            </span>
            <h1 className="text-xl md:text-3xl font-heading font-bold text-foreground-950 mb-4 leading-tight">
              {article.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 md:gap-6 text-xs text-foreground-500">
              <span className="flex items-center gap-1">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-user-line text-xs"></i>
                </div>
                {article.author}
              </span>
              <span className="flex items-center gap-1">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-calendar-line text-xs"></i>
                </div>
                发布于 {article.publishDate}
              </span>
              <span className="flex items-center gap-1">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-refresh-line text-xs"></i>
                </div>
                更新于 {article.updateDate}
              </span>
              <span className="flex items-center gap-1">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-eye-line text-xs"></i>
                </div>
                {article.views.toLocaleString()} 次阅读
              </span>
            </div>
          </div>

          {/* Tags */}
          <div className="flex flex-wrap items-center gap-2 mb-8">
            {article.tags.map((tag) => (
              <span
                key={tag}
                className="inline-block px-3 py-1 bg-secondary-50 text-secondary-700 text-xs rounded-full"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Article Content */}
      <article className="w-full px-4 md:px-6 pb-14 md:pb-20">
        <div className="max-w-3xl mx-auto">
          <div className="prose-custom space-y-5">
            {article.content.map((paragraph, index) => (
              <p
                key={index}
                className="text-sm md:text-base text-foreground-700 leading-relaxed"
              >
                {paragraph}
              </p>
            ))}
          </div>
        </div>
      </article>

      {/* Related Articles */}
      {relatedArticles.length > 0 && (
        <div className="w-full px-4 md:px-6 py-14 bg-background-100 border-t border-background-200/60">
          <div className="max-w-7xl mx-auto">
            <h3 className="text-lg md:text-xl font-heading font-bold text-foreground-950 mb-6">
              相关文章推荐
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 md:gap-6">
              {relatedArticles.map((rel) => (
                <Link
                  key={rel.id}
                  to={`/article/${rel.id}`}
                  className="group bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-primary-200 transition-all duration-300 cursor-pointer"
                >
                  <div className="w-full h-36 overflow-hidden">
                    <img
                      src={rel.coverImage}
                      alt={rel.title}
                      title={rel.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-4">
                    <h4 className="text-sm font-semibold text-foreground-900 mb-1.5 group-hover:text-primary-500 transition-colors duration-200 line-clamp-2 leading-snug">
                      {rel.title}
                    </h4>
                    <p className="text-xs text-foreground-500 line-clamp-2 leading-relaxed">
                      {rel.description}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}